import React, { useState, useEffect, useRef } from 'react'

// Deps
import { connect } from 'react-redux'
import parseISO from 'date-fns/parseISO'
import format from 'date-fns/format';
import compareAsc from 'date-fns/compareAsc'
import tr from 'date-fns/locale/tr'
import XLSX from 'xlsx';

// Partials
import { Table, TR, TD } from 'views/shared/partials/table'
import Loader from 'views/shared/partials/loader'
import Pricebox from 'views/shared/partials/pricebox'
import OrderStatus from 'views/shared/partials/order-status'
import ConversionInfo from 'views/shared/partials/conversion-info'
import Btn from 'views/shared/partials/btn'
import { InputForm, FormInput } from 'views/shared/partials/forms'

// Functions
import formatDate from 'functions/format-date'
import formatNumber from 'functions/format-number'
import getCurrencySymbol from 'functions/get-currency-symbol'

// Utils
import { openModal } from 'utils/modals';

// Services
import orderServices from 'services/order'

// Data
import paymentStatusCodes from 'data/payment-status-codes'
import rawPaymentTypes from 'data/payment-types'
import paymentOrderTypes from 'data/payment-order-types'

// Hooks
import useGroups from 'hooks/groups'
import useSelectQuery from 'hooks/select-query'

const paymentTypes = [
	{ value: '', label: 'Hepsi' },
	...rawPaymentTypes,
]

const getDefaultSelectQuery = () => {
	return  {
		'status_code': paymentStatusCodes[0],
		'payment_type_id': paymentTypes[0],
		'sort_by': paymentOrderTypes[0],
		'store_code': {value: '', label: 'Tüm Gruplar'},
	}
}

const calculateSelectValue = (value, key, groups) => {
	return new Promise((resolve) => {
		if(key === 'status_code') {
			resolve({ key: key, value: paymentStatusCodes.find((opt) => {
				return opt.value === value.toString()
			})});
		}

		else if(key === 'payment_type_id') {
			resolve({ key: key, value: paymentTypes.find((opt) => {
				return opt.value === value.toString()
			})});
		}

		else if(key === 'store_code') {
			resolve({ key: key, value: groups.find((opt) => {
				return opt.value === value.toString()
			})});
		}

		else if(key === 'sort_by') {
			resolve({ key: key, value: paymentOrderTypes.find((opt) => {
				return opt.value === value.toString()
			})});
		}

		else {
			resolve(false);
		}
	});
}

const mapStateToProps = (state, ownProps) => {
	return {
		userGroups: state.user.groups,
		userData: state.user.data,
	};
};

const PaymentList = (props) => {
	const query = props.query;

	const plain = props.plain;

	const searchForm = useRef();

	const [data, setData] = useState(false);
	const [showFilters, setShowFilters] = useState(props.initialFiltersOpen);
	const [excelLoading, setExcelLoading] = useState(false);

	const groups = useGroups(props.userGroups);
	const selectQuery = useSelectQuery(query, groups, calculateSelectValue, getDefaultSelectQuery);

	const [paymentStartDate, setPaymentStartDate] = useState(query.start_date ? parseISO(query.start_date.split(' ')[0]) : false);
	const [paymentDueDate, setPaymentDueDate] = useState(query.due_date ? parseISO(query.due_date.split(' ')[0]) : false);

	const makeSearch = (query) => {
		setData(false);

		orderServices.listPayments(query).then((payload) => {
			setData(payload);
		});
	};

	const getExcel = () => {
		setExcelLoading(true);
		let excelQuery = {...query, page: 1, per_page: 100000};

		orderServices.listPayments(excelQuery).then((payload) => {
			let ws = XLSX.utils.json_to_sheet(payload.payments.map(payment => ({
				id: payment.id,
				payment_type_id: payment.payment_type_id,
				client_order_id: payment.client_order_id,
				currency_exchange_rate_id: payment.currency_exchange_rate_id,
				payment_name: payment.payment_name,
				card_name: payment.card_name,
				card_masked: payment.card_masked,
				card_expiry_date: payment.card_expiry_date,
				installment_count: payment.installment_count,
				status: payment.status,
				price: payment.price,
				price_type: payment?.order?.price_type?.currency_code,
				real_price: payment.real_price,
				refund_total: payment.refund_total,
				currency_exchange_total: payment.currency_exchange_total,
				currency_exchange_refund_total: payment.currency_exchange_refund_total,
				refund_real_total: payment.refund_real_total,
				integrated_at: payment.integrated_at,
				integrated_id: payment.integrated_id,
				error_code: payment.error_code,
				error_message: payment.error_message,
				approve_code: payment.approve_code,
				is_3d: payment.is_3d,
				created_at: payment.created_at,
				payment_type: payment.payment_type?.name,
				currency_exchange_rate: payment.currency_exchange_rate,
				order: payment.order?.business_order_code,
			})));
			let wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "Döküm");

			XLSX.writeFile(wb, format(new Date(), "d MMMM yyyy - HH-mm-ss", { locale: tr }) + " " + props.userData.company.name + " Ödemeler Raporu.xlsx");
		}).catch(() => {

		}).finally(() => {
			setExcelLoading(false);
		})
	}

	useEffect(() => {
		if(paymentStartDate && paymentDueDate) {
			if(compareAsc(paymentStartDate, paymentDueDate) === 1) {
				setPaymentDueDate(false);
			}
		}
	}, [paymentStartDate, paymentDueDate])

	useEffect(() => {
		makeSearch(query);
	}, [query])

	return (
		<section className={'portlet paymentlist ' + (props.className ? props.className : '')}>
			<div className="portlet-header">
				<strong className="portlet-title">Ödemeler</strong>

				{props.plain ?
					<div className="header-controls">
						<Btn white small wide tag="link" href="payments">Tüm İşlemler</Btn>
					</div>
					:
					<div className="header-controls">
						<Btn white small wide icon="plus" onClick={() => { openModal('create-order'); }}>Yeni Talep Oluştur</Btn>
					</div>
				}
			</div>

			{!plain &&
				<InputForm
					ignoreEmpty
					className="portlet-filters"
					ref={searchForm}
					onSubmit={props.onSearch}>
					<div className="filters-searchwrap">
						<FormInput
							className="lower"
							type="text"
							name="search_value"
							value={query.search_value}
							//onChange={(val) => { setFilters({...filters, search_value: val}) }}
							placeholder="Ödemelerde Ara">
						</FormInput>
						<button
							type="submit"
							className="searchwrap-submit">
							<i className="icon-search"></i> ara
						</button>
					</div>
					<Btn
						white small wide
						type="button"
						onClick={() => { setShowFilters(!showFilters)}}
						className="filters-expandbtn"
						icon="filter">
					Detaylı Arama
					</Btn>

					<div className={'filters-more grid-container' + (showFilters ? ' show' : '')}>

						<div className="row">
							<div className="col x3">
								<FormInput
									z="6"
									type="select"
									label="Ödeme Durumu"
									options={paymentStatusCodes}
									defaultValue={selectQuery['status_code'] ? selectQuery['status_code'] : paymentStatusCodes[0]}
									name="status_code">
								</FormInput>
							</div>
							<div className="col x3">
								<FormInput
									z="5"
									type="select"
									label="Ödeme Türü"
									options={paymentTypes}
									defaultValue={selectQuery['payment_type_id'] ? selectQuery['payment_type_id'] : paymentTypes[0]}
									name="payment_type_id">
								</FormInput>
							</div>
							<div className="col x3">
								{<FormInput
									type="select"
									isSearchable
									z="4"
									defaultValue={selectQuery['store_code'] ? selectQuery['store_code'] : groups[0]}
									label="Grup"
									options={groups}
									name="store_code" />}
							</div>
							<div className="col x3">
								<FormInput
									z="3"
									type="select"
									label="Sıralama"
									options={paymentOrderTypes}
									defaultValue={selectQuery['sort_by'] ? selectQuery['sort_by'] : paymentOrderTypes[0]}
									name="sort_by" />
							</div>
						</div>
						<div className="row">
							<div className="col x3">
								<FormInput
									type="date"
									z="2"
									label="Başlangıç Tarihi"
									placeholder="Başlangıç Tarihi Seçin"
									onChange={(value) => { setPaymentStartDate(value); }}
									value={paymentStartDate}
									dateFormat="yyyy-MM-dd 00:00:00"
									name="start_date" />
							</div>
							<div className="col x3">
								<FormInput
									type="date"
									z="1"
									label="Bitiş Tarihi"
									placeholder="Bitiş Tarihi Seçin"
									onChange={(value) => { setPaymentDueDate(value); }}
									minDate={paymentStartDate ? paymentStartDate : undefined}
									value={paymentDueDate}
									dateFormat="yyyy-MM-dd 23:59:59"
									name="end_date" />
							</div>
						</div>
						<div className="row">
							<div className="col more-controls">
								<Btn
									small wide white
									disabled={excelLoading}
									loading={excelLoading}
									type="submit"
									onClick={getExcel}
									icon="excel">
								</Btn>
								<Btn
									small wide white
									type="submit"
									icon="search">
									Sonuçları Getir
								</Btn>
							</div>
						</div>

					</div>
				</InputForm>
			}
			<div className="portlet-section bottom loader-container">
				<Loader loading={!data} />

				{data &&
					<Table
						emptyMessage="Hiç ödeme bulunmuyor."
						className="list-table"
						pagination={!plain ? data.pagination : undefined}
						onPaginate={props.onPaginate}
						heading={
							<React.Fragment>
								<th>Tarih</th>
								<th>Ödeme Bilgileri</th>
								<th>Ödeme Türü</th>
								<th>Ödenen</th>
								{!plain &&
									<React.Fragment>
										<th>Grup Adı</th>
										<th>Müşteri</th>
										<th>Talep Kodu</th>
									</React.Fragment>
								}
								<th>Talep Durumu</th>
								<th></th>
							</React.Fragment>
						}>

						{data.payments.map((payment, nth) => (
							<TR key={nth} mobileTitle={formatDate(payment.created_at, 'dd MMMM yyyy')}>
								<TD className="mobile-full">
									<p className="hide-mobile">
										{formatDate(payment.created_at, 'dd MMMM yyyy')}
									</p>
									<p className="table-subtitle">
										{formatDate(payment.created_at, 'HH:mm')}
									</p>
								</TD>
								<TD mobileTitle="Ödeme Bilgileri" className="mobile-full">
									<p>{payment.card_masked}</p>
									<p className="table-subtitle">
										{payment.card_name} {(payment.card_expiry_date && payment.card_expiry_date.length > 3) && <React.Fragment>- {payment.card_expiry_date}</React.Fragment>}
									</p>
									{payment.approve_code &&
										<p className="table-subtitle">
											İşlem onay kodu: {payment.approve_code}
										</p>
									}
									{plain &&
										<React.Fragment>
											<p className="table-subtitle">
												Grup: {payment.order.group.name}
											</p>
											<p className="table-subtitle">
												Talep kodu: {payment.order.business_order_code}
											</p>
										</React.Fragment>
									}
								</TD>
								<TD className="mobile-full">
									<p>{payment.payment_name}</p>
									{payment.installment_count !== null &&
										<p className="table-subtitle">
											{payment.installment_count !== 1 ? (payment.installment_count + ' Taksit') : 'Tek Çekim'} - %{formatNumber((1 - (payment.price / payment.real_price)) * 100)} Vade
										</p>
									}
									{payment.is_3d &&
										<span className="label success">3D</span>
									}
								</TD>
								<TD mobileTitle="Ödenen">
									<p>
										<Pricebox
											warning
											currency={payment.order.price_type.currency_symbol}
											price={payment.price} />
									</p>
									{(!plain && payment.currency_exchange_total > 0) ?
										<React.Fragment>
											<p>
												<span className="label text-regular">
													<Pricebox
														monochrome
														price={payment.currency_exchange_total} />
												</span>
											</p>
											<p>
												<ConversionInfo
													exchangeType={payment.currency_exchange_rate.currency_exchange_type.id}
													currency={getCurrencySymbol(payment.currency_exchange_rate.currency_code, false, true)}
													price={payment.currency_exchange_rate.currency_exchange_rate} />
											</p>

											{payment.currency_exchange_refund_total > 0 &&
												<p>
													<span className="label error">
														İade : <Pricebox monochrome currency={payment.order.price_type.currency_symbol} price={payment.currency_exchange_refund_total} />
													</span>
												</p>
											}
										</React.Fragment>
									:
										<React.Fragment>
											{(payment.real_price !== payment.price && (parseFloat(payment.real_price) > parseFloat(payment.price))) &&
												<p>
													<span className="label small">
														Çekilen :
														<Pricebox
															monochrome
															price={payment.real_price} />
													</span>
												</p>
											}
											{payment.refund_total > 0 &&
												<p>
													<span className="label small error">
													İade : <Pricebox monochrome price={payment.refund_total} />
													</span>
												</p>
											}
										</React.Fragment>
									}
								</TD>
								{!plain &&
									<TD mobileTitle="Grup Adı">
										<p>{payment.order.group.name}</p>
									</TD>
								}
								{!plain &&
									<TD mobileTitle="Müşteri">
										{payment.order.client.company_name ?
										<>
											<p>
												<strong>{payment.order.client.company_name}</strong><br />
												{payment.order.client.executive_name + ' ' + payment.order.client.executive_surname}
											</p>
										</>
										:
										<p>
											<strong>
												{payment.order.client.executive_name + ' ' + payment.order.client.executive_surname}
											</strong>
										</p>
									}
										<p className="table-subtitle">{payment.order.client.client_code}</p>
									</TD>
								}
								{!plain &&
									<TD mobileTitle="Talep Kodu">
										<p>{payment.order.business_order_code}</p>
									</TD>
								}
								<TD>
									<OrderStatus status={payment.order.status} />
								</TD>
								<TD className="controls">
									<Btn
										small white
										className="small-icon-pre"
										tag="link"
										href="orderDetail"
										params={{ id: payment.order.business_order_code }}
										icon="arrow-right" />
								</TD>
							</TR>
						))}
					</Table>
				}
			</div>
		</section>
	)
}

PaymentList.defaultProps = {
	query: {},
	plain: false,
	initialFiltersOpen: false,
	onPaginate: () => {},
	onSearch: () => {},
}


export default connect(mapStateToProps)(PaymentList);
